import { useEffect, useState } from "react";
import api from "@/lib/api";
import { rupee } from "@/lib/whatsapp";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { Package, Users, ShoppingCart, IndianRupee, AlertTriangle, MessageSquare, Loader2, TrendingUp } from "lucide-react";

const COLORS = ["#2C4C3B", "#E76F51", "#E9C46A", "#A3B18A", "#7F8766", "#3A5A40"];

export default function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <div className="flex justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-forest" /></div>;

  const cards = [
    { label: "Revenue", value: rupee(s.revenue), icon: IndianRupee, color: "#2C4C3B" },
    { label: "Total Orders", value: s.total_orders, icon: ShoppingCart, color: "#E76F51" },
    { label: "Products", value: `${s.active_products}/${s.total_products}`, icon: Package, color: "#7F8766" },
    { label: "Customers", value: s.total_customers, icon: Users, color: "#E9C46A" },
    { label: "Out of Stock", value: s.out_of_stock, icon: AlertTriangle, color: "#E76F51" },
    { label: "Low Stock", value: s.low_stock, icon: AlertTriangle, color: "#E9C46A" },
    { label: "WhatsApp Enq.", value: s.whatsapp_enquiries, icon: MessageSquare, color: "#2C4C3B" },
    { label: "Blog Views", value: s.blog_views, icon: TrendingUp, color: "#A3B18A" },
  ];
  const statusData = Object.entries(s.status_counts || {}).map(([name, value]) => ({ name, value }));

  return (
    <div>
      <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight mb-6">Dashboard</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl soft-shadow p-5" data-testid={`stat-${c.label.toLowerCase().replace(/[^a-z]/g,'')}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-olive font-500">{c.label}</span>
              <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: c.color + "22" }}><c.icon className="w-4.5 h-4.5" style={{ color: c.color }} /></span>
            </div>
            <div className="text-2xl font-600 text-forest mt-2">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl soft-shadow p-5">
          <h3 className="font-heading text-xl text-forest mb-4">Revenue (last 7 days)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={s.sales_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d) => d?.slice(5)} fontSize={11} stroke="#8A8175" />
              <YAxis fontSize={11} stroke="#8A8175" />
              <Tooltip formatter={(v) => rupee(v)} />
              <Bar dataKey="revenue" fill="#2C4C3B" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl soft-shadow p-5">
          <h3 className="font-heading text-xl text-forest mb-4">Orders by Status</h3>
          {statusData.length === 0 ? <p className="text-sm text-muted-foreground">No orders yet</p> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="bg-white rounded-2xl soft-shadow p-5">
          <h3 className="font-heading text-xl text-forest mb-4">Top Products</h3>
          <div className="space-y-3">
            {s.top_products?.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-ink truncate">{i + 1}. {p.name}</span>
                <span className="text-olive shrink-0 ml-2">{p.sold} sold</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl soft-shadow p-5">
          <h3 className="font-heading text-xl text-forest mb-4">Top Categories</h3>
          <div className="space-y-3">
            {s.top_categories?.map((c, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-ink">{c.name}</span>
                <span className="text-olive">{c.count} products</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
